import { cn } from '@/lib/utils';

/**
 * IconGlyph — unicode glyph icon mirror of the prototype's sidebar
 * ornaments. Used where a real icon would feel heavy: sidebar nav
 * markers, agent-watching status block, mono labels.
 *
 * Rationale (plan §3.3 hybrid icon recommendation): keep `lucide-react`
 * for CRUD surfaces (the existing app pattern), use IconGlyph for
 * the prototype's ornament glyphs so the visual tone matches.
 *
 * The glyph map mirrors `NAV_PRIMARY/SECONDARY/ADMIN` from the
 * prototype. Add new glyphs here when a new sidebar/section needs one
 * — keep the type union exhaustive so misnamed glyphs are caught at
 * compile time.
 */

export const ICON_GLYPHS = {
  today: '◐',
  ask: '◇',
  dossiers: '◫',
  scorecard: '▦',
  playbook: '❑',
  rocks: '▣',
  insights: '◉',
  signals: '✦',
  alerts: '▲',
  todos: '✓',
  agent: '✧',
  sources: '⟁',
  bookmarks: '◈',
  discovery: '◔',
  audit: '▤',
  settings: '⚙',
  replay: '↻',
  bell: '◐',
  spark: '✦',
  graph: '⟁',
  watch: '◔',
  flame: '✦',
} as const;

export type IconGlyphName = keyof typeof ICON_GLYPHS;

export function IconGlyph({
  name,
  size = 14,
  className,
}: {
  name: IconGlyphName;
  size?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex select-none items-center justify-center font-normal leading-none',
        className,
      )}
      style={{ fontSize: size, width: size, height: size }}
    >
      {ICON_GLYPHS[name]}
    </span>
  );
}
